/**
 * OpenAI 影像生成提供商設定表單。
 * 與 AiProviderForm(LiteLLM,文字 LLM 給 AI 每日追蹤用)平行獨立。
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  OpenAiImageProviderSettingInputSchema,
  type OpenAiImageProviderSettingInput,
} from '@app/shared';
import {
  useOpenAiImageProviderSetting,
  useTestOpenAiImageProvider,
  useUpdateOpenAiImageProvider,
} from './openaiImageProviderApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function OpenAiImageProviderForm(): JSX.Element {
  const { data, isLoading, isError, error } = useOpenAiImageProviderSetting();
  const updateMutation = useUpdateOpenAiImageProvider();
  const testMutation = useTestOpenAiImageProvider();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<OpenAiImageProviderSettingInput>({
    resolver: zodResolver(OpenAiImageProviderSettingInputSchema),
    defaultValues: {
      enabled: false,
      baseUrl: 'https://api.openai.com/v1',
      apiKey: '',
      model: 'gpt-image-1',
      dailyCostUsd: 5,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        enabled: data.enabled,
        baseUrl: data.baseUrl,
        apiKey: '',
        model: data.model,
        dailyCostUsd: data.dailyCostUsd,
      });
    }
  }, [data, reset]);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: OpenAiImageProviderSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => {
        setSavedFlash('已儲存');
        reset({ ...values, apiKey: '' });
      },
    });
  };

  const errMsg = (e: unknown): string => getApiErrorMessage(e, '未知錯誤');

  return (
    <section className="grid gap-4 max-w-xl">
      <h2 className="text-lg font-semibold">OpenAI 提供商(生圖)</h2>
      <p className="text-sm text-slate-600">
        專責生圖用(gpt-image-1 / dall-e-3),與「AI 提供商」獨立。
        API Key 以 AES-256-GCM 加密儲存;留空代表保留原 Key。
        測試連線會打 <code>GET /v1/models</code>,不會產生生圖費用。
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('enabled')} /> 啟用 OpenAI 生圖
        </label>

        <Row label="Base URL" error={errors.baseUrl?.message}>
          <input
            {...register('baseUrl')}
            className={inputCls}
            placeholder="https://api.openai.com/v1"
          />
        </Row>

        <Row
          label={`API Key${data?.hasApiKey ? '(已設定,留空保留原值)' : ''}`}
          error={errors.apiKey?.message}
        >
          <input
            type="password"
            {...register('apiKey')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasApiKey ? '(留空保留原 Key)' : '(未設定)'}
          />
        </Row>

        <Row label="模型" error={errors.model?.message}>
          <input
            {...register('model')}
            className={inputCls}
            placeholder="gpt-image-1"
          />
        </Row>

        <Row label="每日成本上限(USD)" error={errors.dailyCostUsd?.message}>
          <input
            type="number"
            step="0.01"
            min="0"
            {...register('dailyCostUsd', { valueAsNumber: true })}
            className={`${inputCls} w-32`}
          />
        </Row>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            儲存失敗：{errMsg(updateMutation.error)}
          </div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
          <button
            type="button"
            disabled={testMutation.isPending || isDirty}
            title={isDirty ? '請先儲存再測試' : '打 GET /v1/models 驗證連線'}
            onClick={() => testMutation.mutate()}
            className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            {testMutation.isPending ? '測試中…' : '測試連線'}
          </button>
        </div>

        {testMutation.isSuccess && testMutation.data ? (
          testMutation.data.ok ? (
            <div role="status" className="text-green-700 text-sm">
              ✓ 連線成功({testMutation.data.latencyMs ?? 0} ms,
              可用模型 {testMutation.data.modelCount ?? 0} 個)
            </div>
          ) : (
            <div role="alert" className="text-red-600 text-sm">
              ✗ 連線失敗：{testMutation.data.message}
            </div>
          )
        ) : null}
        {testMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            測試失敗：{errMsg(testMutation.error)}
          </div>
        ) : null}
      </form>
    </section>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? <span role="alert" className="text-red-600 text-xs">{error}</span> : null}
    </label>
  );
}

const inputCls = 'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
